import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ClassStudentSelect } from "@/components/ClassStudentSelect";
import { GRADE_GROUPS } from "@/lib/subjects";
import KurumSinavPanel, { LgsStatsEditor, PuanNote } from "@/pages/KurumSinav";
import { ScanLine, Plus, Trash2, Pencil, Save, X, FileCheck2, BarChart3, CheckCircle2, XCircle, MinusCircle, Printer } from "lucide-react";
import SonucBelgesi from "@/components/SonucBelgesi";

const OPTS = ["A", "B", "C", "D"];
const blankSubject = () => ({ name: "", question_count: 10, answer_key: "", kazanims: [] });

export default function OptikSinav() {
  const { user } = useAuth();
  const isEditor = user?.role === "admin" || user?.role === "koc";
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [grade, setGrade] = useState(5);
  const [tab, setTab] = useState("exams"); // exams | mark | eval
  const [editExam, setEditExam] = useState(null);
  const [activeExam, setActiveExam] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [evalRows, setEvalRows] = useState(null);
  const [karne, setKarne] = useState(null);
  const [myCtx, setMyCtx] = useState(null);
  const [bulkAnswers, setBulkAnswers] = useState({});   // { student_id: { subject: "ABCD" } }
  const [subjOptions, setSubjOptions] = useState([]);
  const [searchParams] = useSearchParams();
  const impId = searchParams.get("student");

  const loadExams = (g) => api.get("/optik-exams", { params: isEditor ? { grade: g } : {} }).then(({ data }) => setExams(data));
  useEffect(() => {
    if (isEditor) { api.get("/students").then(({ data }) => setStudents(data)); loadExams(grade); api.get("/subjects", { params: { grade } }).then(({ data }) => setSubjOptions(data)); }
    else { api.get("/my/context").then(({ data }) => setMyCtx(data)); loadExams(); }
  }, [isEditor, grade]);

  useEffect(() => {
    if (isEditor && impId && students.length) {
      const st = students.find((s) => s.id === impId);
      if (st) api.get("/optik-exams", { params: { grade: st.grade } }).then(({ data }) => setExams(data));
    }
  }, [isEditor, impId, students]);

  // ---- Admin: exam create/edit ----
  const newExam = () => setEditExam({ name: "", date: "", grade, subjects: [blankSubject()] });
  const editExisting = (e) => setEditExam(JSON.parse(JSON.stringify(e)));
  const patchSubj = (si, patch) => setEditExam((ex) => ({ ...ex, subjects: ex.subjects.map((s, i) => (i === si ? { ...s, ...patch } : s)) }));
  const setKey = (si, qi, val) => setEditExam((ex) => ({ ...ex, subjects: ex.subjects.map((s, i) => { if (i !== si) return s; const k = (s.answer_key || "").padEnd(s.question_count, " ").split(""); k[qi] = val; return { ...s, answer_key: k.join("").trimEnd() }; }) }));
  const setKz = (si, qi, val) => setEditExam((ex) => ({ ...ex, subjects: ex.subjects.map((s, i) => { if (i !== si) return s; const kz = [...(s.kazanims || [])]; while (kz.length < s.question_count) kz.push(""); kz[qi] = val; return { ...s, kazanims: kz }; }) }));

  const saveExam = async () => {
    if (!editExam.name.trim()) return toast.error("Sınav adı gerekli");
    try {
      if (editExam.id) await api.put(`/optik-exams/${editExam.id}`, editExam);
      else await api.post("/optik-exams", editExam);
      toast.success("Sınav kaydedildi ✓");
      setEditExam(null); loadExams(grade);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delExam = async (id) => { if (!window.confirm("Sınav ve tüm sonuçları silinsin mi?")) return; await api.delete(`/optik-exams/${id}`); toast.success("Silindi"); loadExams(grade); };

  // ---- Admin: optik işaretleme ----
  const openMark = (exam) => { setActiveExam(exam); setStudentId(null); setAnswers({}); setResult(null); setTab("mark"); };
  useEffect(() => {
    if (tab === "mark" && activeExam && studentId) {
      api.get("/optik-results", { params: { exam_id: activeExam.id, student_id: studentId } }).then(({ data }) => {
        if (data[0]) { setAnswers(data[0].answers || {}); setResult(data[0]); } else { setAnswers({}); setResult(null); }
      });
    }
  }, [tab, activeExam, studentId]);
  const markAns = (subj, qi, val) => setAnswers((a) => { const s = (a[subj] || "").padEnd(activeExam.subjects.find((x) => x.name === subj).question_count, " ").split(""); s[qi] = s[qi] === val ? " " : val; return { ...a, [subj]: s.join("") }; });
  const saveMark = async () => {
    if (!studentId) return toast.error("Öğrenci seçin");
    try { const { data } = await api.post("/optik-results", { exam_id: activeExam.id, student_id: studentId, answers }); setResult(data); toast.success("Optik kaydedildi & değerlendirildi ✓"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Değerlendirme (sınıflar arası) ----
  const openEval = (exam) => { setActiveExam(exam); api.get(`/optik-exams/${exam.id}/evaluation`).then(({ data }) => setEvalRows(data.rows)); setTab("eval"); };

  // ---- Toplu optik işaretleme (tüm sınıf) ----
  const openBulk = (exam) => {
    setActiveExam(exam); setTab("bulk");
    api.get("/optik-results", { params: { exam_id: exam.id } }).then(({ data }) => {
      const m = {}; data.forEach((r) => { m[r.student_id] = r.answers || {}; }); setBulkAnswers(m);
    });
  };
  const setBulkAns = (sid, subj, val, qc) => setBulkAnswers((b) => ({ ...b, [sid]: { ...(b[sid] || {}), [subj]: val.toUpperCase().replace(/[^ABCD ]/g, "").slice(0, qc) } }));
  const saveBulk = async () => {
    const results = Object.entries(bulkAnswers).map(([student_id, answers]) => ({ student_id, answers }));
    try {
      const { data } = await api.post("/optik-results/bulk", { exam_id: activeExam.id, results });
      toast.success(`${data.count} öğrenci değerlendirildi ✓`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Karne ----
  const openKarne = async (exam, sid) => {
    setKarneSid(sid);
    try {
      const { data } = await api.get(`/optik-exams/${exam.id}/karne`, { params: sid ? { student_id: sid } : {} });
      setKarne(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [examRank, setExamRank] = useState({});
  const [karneSid, setKarneSid] = useState(null);
  const toggleRank = async (examId) => {
    if (examRank[examId]) { setExamRank((m) => ({ ...m, [examId]: null })); return; }
    try { const { data } = await api.get(`/optik-exams/${examId}/evaluation`); setExamRank((m) => ({ ...m, [examId]: data.rows })); }
    catch { toast.error("Sıralama yüklenemedi"); }
  };

  const inp = "rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400";

  // STUDENT/VELI VIEW (+ admin ?student= canlı görünüm)
  const impersonate = isEditor && impId;
  const impSt = impersonate ? students.find((s) => s.id === impId) : null;
  if (!isEditor || impersonate) {
    const viewSid = impersonate ? impId : myCtx?.student_id;
    return (
      <div className="mx-auto max-w-4xl space-y-5">
        <Header />
        {impersonate && <p data-testid="optik-impersonate-note" className="rounded-xl bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700">{impSt?.name || "Öğrenci"} — öğrenci görünümü (salt-okur)</p>}
        <div className="flex gap-2">
          {[["exams", "Optik Sınavlar"], ["kurum", "Kurum Sınavı"]].map(([k, l]) => (
            <button key={k} data-testid={`optik-student-tab-${k}`} onClick={() => setTab(k)} className={`rounded-xl px-4 py-2 text-sm font-bold transition ${tab === k ? "bg-[#1E293B] text-white" : "border border-slate-200 bg-white text-slate-600"}`}>{l}</button>
          ))}
        </div>
        {tab === "kurum" ? <KurumSinavPanel grade={null} isEditor={false} students={[]} viewGrade={impSt?.grade ?? null} viewStudentId={impersonate ? impId : null} /> : (
          exams.length === 0 ? <Empty text="Henüz yayınlanmış sınav yok." /> : exams.map((e) => (
            <div key={e.id} data-testid={`optik-exam-${e.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="font-bold text-slate-800">{e.name}</p><p className="text-xs text-slate-500">{e.date} · {e.grade}. Sınıf · {e.subjects?.length} ders</p></div>
                <div className="flex gap-2">
                  <button data-testid={`optik-rank-${e.id}`} onClick={() => toggleRank(e.id)} className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600 transition hover:border-indigo-300 hover:text-indigo-600 active:scale-95"><BarChart3 className="h-4 w-4" /> Sıralama</button>
                  <button data-testid={`optik-karne-${e.id}`} onClick={() => openKarne(e, viewSid)} className="flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 active:scale-95"><FileCheck2 className="h-4 w-4" /> Sınav Karnem</button>
                </div>
              </div>
              {examRank[e.id] && (
                <div className="mt-3 overflow-hidden rounded-xl border border-slate-100">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 font-bold text-slate-500"><tr><th className="px-3 py-1.5 text-left">#</th><th className="px-3 py-1.5 text-left">Öğrenci</th><th className="px-2 py-1.5">D</th><th className="px-2 py-1.5">Y</th><th className="px-2 py-1.5">B</th><th className="px-2 py-1.5">Net</th><th className="px-2 py-1.5">Puan</th></tr></thead>
                    <tbody>
                      {examRank[e.id].map((r, i) => (
                        <tr key={r.student_id} data-testid={`optik-rank-row-${e.id}-${i}`} className={`border-t border-slate-50 ${r.student_id === viewSid ? "bg-indigo-50 font-bold" : ""}`}>
                          <td className="px-3 py-1 text-slate-400">{i + 1}</td><td className="px-3 py-1">{r.name}{r.section ? ` /${r.section}` : ""}</td><td className="text-center text-emerald-600">{r.total?.dogru}</td><td className="text-center text-rose-500">{r.total?.yanlis}</td><td className="text-center text-slate-400">{r.total?.bos}</td><td className="text-center font-extrabold text-indigo-600">{r.total?.net}</td><td className="text-center font-bold text-[#16324F]">{r.puan ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <PuanNote rows={examRank[e.id]} />
                </div>
              )}
            </div>
          ))
        )}
        {karne && <KarneModal karne={karne} onClose={() => setKarne(null)} isEditor={isEditor} onChanged={() => openKarne({ id: karne.exam.id }, karneSid)} />}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Header />
        <div className="flex items-center gap-2">
          <select data-testid="optik-grade-select" value={grade} onChange={(e) => setGrade(Number(e.target.value))} className={inp}>
            {GRADE_GROUPS.map((g) => <optgroup key={g.label} label={g.label}>{g.grades.map((gr) => <option key={gr} value={gr}>{gr}. Sınıf</option>)}</optgroup>)}
          </select>
          {tab === "exams" && <button data-testid="optik-new-btn" onClick={newExam} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Plus className="h-5 w-5" /> Yeni Sınav</button>}
        </div>
      </div>

      <div className="flex gap-2">
        {[["exams", "Sınavlar"], ["mark", "Optik İşaretle"], ["bulk", "Toplu İşaretle"], ["eval", "Sınıflar Arası Sonuç"], ["kurum", "Kurum Sınavı"]].map(([k, l]) => (
          <button key={k} data-testid={`optik-tab-${k}`} onClick={() => setTab(k)} className={`rounded-xl px-4 py-2 text-sm font-bold transition ${tab === k ? "bg-[#1E293B] text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
        ))}
      </div>

      {tab === "exams" && (
        <div className="space-y-3">
          {exams.length === 0 && <Empty text="Bu sınıfta sınav yok — 'Yeni Sınav' ile ekleyin." />}
          {exams.map((e) => (
            <div key={e.id} data-testid={`optik-exam-${e.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div><p className="font-bold text-slate-800">{e.name}</p><p className="text-xs text-slate-500">{e.date || "tarih yok"} · {e.grade}. Sınıf · {e.subjects?.map((s) => `${s.name}(${s.question_count})`).join(", ")}</p></div>
              <div className="flex items-center gap-1">
                <button data-testid={`optik-mark-${e.id}`} onClick={() => openMark(e)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"><ScanLine className="inline h-4 w-4" /> Optik</button>
                <button data-testid={`optik-bulk-${e.id}`} onClick={() => openBulk(e)} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700"><ScanLine className="inline h-4 w-4" /> Toplu</button>
                <button data-testid={`optik-eval-${e.id}`} onClick={() => openEval(e)} className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-sky-700"><BarChart3 className="inline h-4 w-4" /> Sonuç</button>
                <button data-testid={`optik-edit-${e.id}`} onClick={() => editExisting(e)} className="rounded-lg p-2 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`optik-del-${e.id}`} onClick={() => delExam(e.id)} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "mark" && (
        activeExam ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="mb-2 font-bold text-slate-800">{activeExam.name} — Optik İşaretle</p>
              <ClassStudentSelect students={students.filter((s) => s.grade === activeExam.grade)} value={studentId} onChange={setStudentId} testid="optik-student-select" placeholder="Öğrenci seçin…" />
            </div>
            {studentId && activeExam.subjects.map((subj) => (
              <div key={subj.name} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h4 className="mb-3 font-bold text-slate-700">{subj.name} <span className="text-xs font-normal text-slate-400">({subj.question_count} soru · cevap anahtarıyla karşılaştırılır)</span></h4>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: subj.question_count }).map((_, qi) => {
                    const cur = (answers[subj.name] || "")[qi];
                    return (
                      <div key={qi} data-testid={`optik-q-${subj.name}-${qi}`} className="flex flex-col items-center gap-0.5 rounded-lg border border-slate-100 p-1.5">
                        <span className="text-[10px] font-bold text-slate-400">{qi + 1}</span>
                        <div className="flex gap-0.5">
                          {OPTS.map((o) => (
                            <button key={o} data-testid={`optik-q-${subj.name}-${qi}-${o}`} onClick={() => markAns(subj.name, qi, o)} className={`h-6 w-6 rounded-full border text-[11px] font-bold transition ${cur === o ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 text-slate-500 hover:border-indigo-400"}`}>{o}</button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {studentId && (
              <div className="flex items-center gap-3">
                <button data-testid="optik-save-mark" onClick={saveMark} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white transition hover:bg-emerald-700 active:scale-95"><Save className="h-5 w-5" /> Kaydet & Değerlendir</button>
                {result && <button data-testid="optik-view-karne" onClick={() => openKarne(activeExam, studentId)} className="flex items-center gap-2 rounded-xl border border-indigo-200 px-5 py-3 font-bold text-indigo-600 hover:bg-indigo-50"><FileCheck2 className="h-5 w-5" /> Karneyi Gör</button>}
              </div>
            )}
            {result && <div data-testid="optik-mark-result" className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700">Toplam → D:{result.total.dogru} · Y:{result.total.yanlis} · B:{result.total.bos} · Net {result.total.net}</div>}
          </div>
        ) : <Empty text="Sınavlar sekmesinden bir sınavın 'Optik' butonuna basın." />
      )}

      {tab === "bulk" && (
        activeExam ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div>
                <p className="font-bold text-slate-800">{activeExam.name} — Toplu Optik İşaretle</p>
                <p className="text-xs text-slate-500">{activeExam.grade}. Sınıf · her öğrenci için ders cevaplarını yazın (ör. ABCDA). Boş bırakılan öğrenci kaydedilmez.</p>
              </div>
              <button data-testid="optik-bulk-save" onClick={saveBulk} className="flex items-center gap-2 rounded-xl bg-amber-600 px-5 py-2.5 font-bold text-white transition hover:bg-amber-700 active:scale-95"><Save className="h-5 w-5" /> Tümünü Kaydet & Değerlendir</button>
            </div>
            {(() => {
              const cls = students.filter((s) => s.grade === activeExam.grade);
              if (cls.length === 0) return <Empty text="Bu sınıfta öğrenci yok." />;
              return (
                <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs font-bold text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left">Öğrenci</th>
                        {activeExam.subjects.map((s) => <th key={s.name} className="px-3 py-2 text-left">{s.name}<span className="ml-1 font-normal text-slate-400">({s.question_count})</span></th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {cls.map((st) => (
                        <tr key={st.id} data-testid={`optik-bulk-row-${st.id}`} className="border-t border-slate-100">
                          <td className="whitespace-nowrap px-4 py-2 font-semibold text-slate-700">{st.name}<span className="ml-1 text-xs text-slate-400">{st.section ? `/${st.section}` : ""}</span></td>
                          {activeExam.subjects.map((subj) => (
                            <td key={subj.name} className="px-3 py-2">
                              <input
                                data-testid={`optik-bulk-input-${st.id}-${subj.name}`}
                                value={(bulkAnswers[st.id]?.[subj.name]) || ""}
                                onChange={(e) => setBulkAns(st.id, subj.name, e.target.value, subj.question_count)}
                                placeholder={"—".repeat(Math.min(subj.question_count, 8))}
                                className="w-36 rounded-lg border border-slate-200 px-2.5 py-1.5 font-mono text-sm uppercase tracking-widest outline-none focus:border-amber-400"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        ) : <Empty text="Sınavlar sekmesinden bir sınavın 'Toplu' butonuna basın." />
      )}

      {tab === "eval" && (
        activeExam && evalRows ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-3 font-bold text-slate-800">{activeExam.name} — Sınıflar Arası Sonuç ({evalRows.length} öğrenci)</div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-bold text-slate-500"><tr><th className="px-4 py-2 text-left">#</th><th className="px-4 py-2 text-left">Öğrenci</th><th className="px-4 py-2">Sınıf</th><th className="px-4 py-2">Doğru</th><th className="px-4 py-2">Yanlış</th><th className="px-4 py-2">Boş</th><th className="px-4 py-2">Net</th><th className="px-4 py-2">LGS Puanı</th></tr></thead>
              <tbody>
                {evalRows.map((r, i) => (
                  <tr key={r.student_id} data-testid={`optik-eval-row-${i}`} className="border-t border-slate-100"><td className="px-4 py-2 font-bold text-slate-400">{i + 1}</td><td className="px-4 py-2 font-semibold text-slate-700">{r.name}</td><td className="px-4 py-2 text-center">{r.grade}{r.section ? `/${r.section}` : ""}</td><td className="px-4 py-2 text-center text-emerald-600 font-bold">{r.total.dogru}</td><td className="px-4 py-2 text-center text-rose-600 font-bold">{r.total.yanlis}</td><td className="px-4 py-2 text-center">{r.total.bos}</td><td className="px-4 py-2 text-center font-extrabold text-indigo-600">{r.total.net}</td><td data-testid={`optik-eval-puan-${i}`} className="px-4 py-2 text-center font-bold text-[#16324F]">{r.puan ?? "—"}</td></tr>
                ))}
              </tbody>
            </table>
            <PuanNote rows={evalRows} />
            {evalRows.length === 0 && <p className="py-6 text-center text-slate-400">Henüz optik işaretlenmiş öğrenci yok.</p>}
          </div>
        ) : <Empty text="Sınavlar sekmesinden bir sınavın 'Sonuç' butonuna basın." />
      )}

      {/* Exam editor modal */}
      {tab === "kurum" && <KurumSinavPanel grade={grade} isEditor={isEditor} students={students} />}

      {editExam && <ExamEditor exam={editExam} setEx={setEditExam} patchSubj={patchSubj} setKey={setKey} setKz={setKz} onSave={saveExam} onClose={() => setEditExam(null)} subjOptions={subjOptions} />}
      {karne && <KarneModal karne={karne} onClose={() => setKarne(null)} isEditor={isEditor} onChanged={() => openKarne({ id: karne.exam.id }, karneSid)} />}
    </div>
  );
}

const Header = () => (
  <div className="flex items-center gap-3">
    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-fuchsia-100 text-fuchsia-600"><ScanLine className="h-6 w-6" /></div>
    <div><h1 className="font-display text-3xl font-extrabold text-slate-900">Canlı Optik Sınav</h1><p className="text-slate-500">Optik işaretleme, cevap anahtarı, kazanım analizi ve öğrenciye özel sınav karnesi.</p></div>
  </div>
);
const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">{text}</div>;

function ExamEditor({ exam, setEx, patchSubj, setKey, setKz, onSave, onClose, subjOptions = [] }) {
  const OPTS2 = ["A", "B", "C", "D", " "];
  const inp = "rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400";
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="my-8 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-xl font-bold">{exam.id ? "Sınavı Düzenle" : "Yeni Optik Sınav"}</h3><button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button></div>
        <div className="grid grid-cols-3 gap-3">
          <input data-testid="optik-ex-name" value={exam.name} onChange={(e) => setEx({ ...exam, name: e.target.value })} placeholder="Sınav adı" className={`${inp} col-span-2`} />
          <input data-testid="optik-ex-date" type="date" value={exam.date} onChange={(e) => setEx({ ...exam, date: e.target.value })} className={inp} />
        </div>
        <div className="mt-4 space-y-4">
          {exam.subjects.map((s, si) => (
            <div key={si} className="rounded-xl border border-slate-200 p-3">
              <div className="mb-2 flex items-center gap-2">
                <input data-testid={`optik-subj-name-${si}`} list="optik-subj-list" value={s.name} onChange={(e) => patchSubj(si, { name: e.target.value })} placeholder="Ders adı (listeden seçin veya yazın)" className={`${inp} flex-1`} />
                <label className="text-xs font-bold text-slate-500">Soru:</label>
                <input data-testid={`optik-subj-qc-${si}`} type="number" min={1} max={50} value={s.question_count} onChange={(e) => patchSubj(si, { question_count: Number(e.target.value) || 1 })} className={`${inp} w-16`} />
                <button onClick={() => setEx({ ...exam, subjects: exam.subjects.filter((_, j) => j !== si) })} className="rounded-lg p-1.5 text-rose-400 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {Array.from({ length: s.question_count }).map((_, qi) => (
                  <div key={qi} className="flex items-center gap-2 text-sm">
                    <span className="w-6 text-right font-bold text-slate-400">{qi + 1}.</span>
                    <select data-testid={`optik-key-${si}-${qi}`} value={(s.answer_key || "")[qi] || " "} onChange={(e) => setKey(si, qi, e.target.value)} className="rounded border border-slate-200 px-1.5 py-1 text-xs font-bold">
                      {OPTS2.map((o) => <option key={o} value={o}>{o === " " ? "—" : o}</option>)}
                    </select>
                    <input data-testid={`optik-kz-${si}-${qi}`} value={(s.kazanims || [])[qi] || ""} onChange={(e) => setKz(si, qi, e.target.value)} placeholder={`${qi + 1}. soru kazanımı`} className="flex-1 rounded border border-slate-200 px-2 py-1 text-xs outline-none focus:border-indigo-400" />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <LgsStatsEditor value={exam.lgs_stats} onChange={(v) => setEx({ ...exam, lgs_stats: v })} subjects={exam.subjects.filter((s) => s.name)} />
          <button data-testid="optik-add-subj" onClick={() => setEx({ ...exam, subjects: [...exam.subjects, blankSubject()] })} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 py-2 text-sm font-bold text-slate-500 hover:border-indigo-400 hover:text-indigo-600"><Plus className="h-4 w-4" /> Ders Ekle</button>
        </div>
        <button data-testid="optik-save-exam" onClick={onSave} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Save className="h-5 w-5" /> Kaydet</button>
        <datalist id="optik-subj-list">
          {subjOptions.map((s) => <option key={s.id} value={s.name} />)}
        </datalist>
      </div>
    </div>
  );
}

export function KarneModal({ karne, onClose, isEditor = false, onChanged = null }) {
  const { exam, result, student, stats, analysis, history, puan } = karne;
  const doPrint = () => {
    document.body.classList.add("print-karne");
    const done = () => { document.body.classList.remove("print-karne"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    window.print();
  };
  const addExtra = async (subject, kazanim) => {
    try {
      await api.post("/optik-results/extra-topic", { subject, kazanim }, { params: { exam_id: karne.exam?.id, student_id: karne.student?.id } });
      toast.success("Kazanım eklendi ✓");
      if (onChanged) onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeExtra = async (subject, kazanim) => {
    try {
      await api.delete("/optik-results/extra-topic", { params: { exam_id: karne.exam?.id, student_id: karne.student?.id, subject, kazanim } });
      if (onChanged) onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const subjects = Object.entries(result.per_subject || {}).map(([name, sc]) => ({
    name, soru: sc.soru, dogru: sc.dogru, yanlis: sc.yanlis, bos: sc.bos, net: sc.net,
    rank: analysis?.[name]?.rank, basari: sc.soru ? Math.round((sc.net / sc.soru) * 100) : 0,
    class_pct: analysis?.[name]?.class_pct, detay: sc.detay || [], topics: analysis?.[name]?.topics || [],
    extra: (result.extra_topics || []).filter((t) => t.subject === name).map((t) => t.kazanim),
  }));
  const d = {
    docTitle: "SINAV SONUÇ BELGESİ",
    examName: exam.name, subInfo: exam.date,
    student, total: result.total, maxNet: stats?.total_questions || 0,
    circles: [
      { label: `Netiniz (Maks:${stats?.total_questions ?? "—"})`, value: result.total.net, color: "#16324F" },
      { label: "Sınıf Ortalaması", value: stats?.avg_net ?? "—", color: "#64748B" },
      { label: "En Yüksek Net", value: stats?.max_net ?? "—", color: "#94A3B8" },
    ],
    ranks: [{ label: "Sınıf", count: stats?.count, rank: stats?.rank }],
    history: history || [],
    subjects,
    onExtraAdd: isEditor ? addExtra : null, onExtraRemove: isEditor ? removeExtra : null,
    puan: puan || null,
  };
  return (
    <div className="karne-overlay fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <div data-testid="optik-karne-modal" className="karne-print my-8 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="no-print mb-3 flex items-center justify-end gap-2">
          <button data-testid="optik-karne-print" onClick={doPrint} className="flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Printer className="h-4 w-4" /> Yazdır / PDF</button>
          <button data-testid="optik-karne-close" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <SonucBelgesi d={d} />
      </div>
    </div>
  );
}
